import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Clock, HelpCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import api from '@/lib/api';
import { countryCodes } from '@/lib/countries';
import { useLang } from '@/contexts/LangContext';
import { useAuth } from '@/contexts/AuthContext';

function pickText(lang, arText, enText) {
  return lang === 'ar' ? arText : enText;
}

function isPlacementLanguage(language) {
  const code = String(language?.code || '').trim().toLowerCase();
  const name = String(language?.name || '').trim().toLowerCase();
  return code === 'ar' || code === 'en' || name === 'arabic' || name === 'english';
}

function resolveLocalizedValue(value, uiLang) {
  if (!value) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return resolveLocalizedValue(JSON.parse(trimmed), uiLang);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  if (typeof value === 'object') {
    const preferredKeys = uiLang === 'ar'
      ? ['ar_001', 'ar_SY', 'ar', 'en_US']
      : ['en_US', 'en', 'ar_001', 'ar'];

    for (const key of preferredKeys) {
      if (typeof value[key] === 'string' && value[key].trim()) {
        return value[key].trim();
      }
    }

    const fallbackValue = Object.values(value).find((entry) => typeof entry === 'string' && entry.trim());
    return fallbackValue ? fallbackValue.trim() : '';
  }

  return String(value);
}

function getLocalizedLanguageName(language, uiLang) {
  const code = String(language?.code || '').trim().toLowerCase();
  const localizedNames = {
    ar: { ar: 'اللغة العربية', en: 'Arabic' },
    en: { ar: 'اللغة الإنجليزية', en: 'English' },
    fr: { ar: 'اللغة الفرنسية', en: 'French' },
  };

  if (localizedNames[code]?.[uiLang]) {
    return localizedNames[code][uiLang];
  }

  return language?.name || '';
}

export default function PlacementTestPage() {
  const [step, setStep] = useState('intro'); // intro, test, info, submitting, result
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [resumeAfterInfo, setResumeAfterInfo] = useState(false);
  const [info, setInfo] = useState({ full_name: '', email: '', phone: '', country_code: '+971', notes: '' });
  const [languages, setLanguages] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);
  const [templateIntro, setTemplateIntro] = useState('');
  const [showIntro, setShowIntro] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryLanguageId = searchParams.get('language_id') || '';
  const queryCourseTypeId = searchParams.get('course_type_id') || '';
  const [selection, setSelection] = useState(() => ({
    language_id: queryLanguageId,
    course_type_id: queryCourseTypeId,
  }));

  const resetPlacementFlow = () => {
    setStep('intro');
    setShowIntro(false);
    setResumeAfterInfo(false);
    setResult(null);
    setCurrentQ(0);
    setAnswers({});
  };

  useEffect(() => {
    api.get('/languages')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setLanguages(list.filter(isPlacementLanguage));
      })
      .catch(() => setLanguages([]));
  }, [lang]);

  useEffect(() => {
    setSelection((prev) => {
      if (prev.language_id === queryLanguageId && prev.course_type_id === queryCourseTypeId) {
        return prev;
      }
      return { language_id: queryLanguageId, course_type_id: queryCourseTypeId };
    });
  }, [queryCourseTypeId, queryLanguageId]);

  useEffect(() => {
    const hasSelectedLanguage = languages.some((item) => String(item.id) === String(selection.language_id));
    if ((!selection.language_id || !hasSelectedLanguage) && languages.length > 0) {
      const englishLanguage = languages.find((item) => (item.code || '').toLowerCase() === 'en' || (item.name || '').toLowerCase() === 'english');
      const fallbackLanguage = englishLanguage || languages[0];
      if (fallbackLanguage?.id) {
        const nextLanguageId = String(fallbackLanguage.id);
        setSelection((current) => (
          current.language_id === nextLanguageId && !current.course_type_id
            ? current
            : { language_id: nextLanguageId, course_type_id: '' }
        ));
      }
    }
  }, [languages, selection.language_id]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    let changed = false;

    if (selection.language_id) {
      if (nextParams.get('language_id') !== selection.language_id) {
        nextParams.set('language_id', selection.language_id);
        changed = true;
      }
    } else if (nextParams.has('language_id')) {
      nextParams.delete('language_id');
      changed = true;
    }

    if (selection.course_type_id) {
      if (nextParams.get('course_type_id') !== selection.course_type_id) {
        nextParams.set('course_type_id', selection.course_type_id);
        changed = true;
      }
    } else if (nextParams.has('course_type_id')) {
      nextParams.delete('course_type_id');
      changed = true;
    }

    if (changed) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, selection.course_type_id, selection.language_id, setSearchParams]);

  useEffect(() => {
    if (!selection.language_id) {
      setCourseTypes([]);
      setSelection((current) => (
        current.course_type_id ? { ...current, course_type_id: '' } : current
      ));
      return;
    }

    let ignore = false;
    const requestLanguageId = selection.language_id;
    api.get('/course-types', { params: { language_id: selection.language_id } })
      .then((res) => {
        if (ignore) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setCourseTypes(list);
        setSelection((current) => {
          if (String(current.language_id) !== String(requestLanguageId)) {
            return current;
          }
          const hasCurrentCourseType = list.some((item) => String(item.id) === String(current.course_type_id));
          const nextCourseTypeId = hasCurrentCourseType ? current.course_type_id : (list[0]?.id ? String(list[0].id) : '');
          if (current.course_type_id === nextCourseTypeId) {
            return current;
          }
          return { ...current, course_type_id: nextCourseTypeId };
        });
      })
      .catch(() => {
        if (ignore) return;
        setCourseTypes([]);
        setSelection((current) => (
          current.course_type_id ? { ...current, course_type_id: '' } : current
        ));
      });

    return () => {
      ignore = true;
    };
  }, [lang, selection.language_id]);

  useEffect(() => {
    if (!selection.language_id || !selection.course_type_id) {
      setQuestions([]);
      setTemplateIntro('');
      setLoadingQuestions(false);
      return;
    }

    let ignore = false;
    const params = {};
    if (selection.language_id) params.language_id = selection.language_id;
    if (selection.course_type_id) params.course_type_id = selection.course_type_id;
    params.include_template = 1;
    setLoadingQuestions(true);
    api.get('/placement-test/questions', { params })
      .then(res => {
        if (ignore) return;
        const payload = res.data || {};
        const list = Array.isArray(payload) ? payload : (payload.questions || []);
        setQuestions(list);
        setTemplateIntro(Array.isArray(payload) ? '' : (payload.template?.introduction_html || ''));
        setCurrentQ(0);
        setAnswers({});
      })
      .catch(() => {
        if (ignore) return;
        setQuestions([]);
        setTemplateIntro('');
      })
      .finally(() => {
        if (ignore) return;
        setLoadingQuestions(false);
      });

    return () => {
      ignore = true;
    };
  }, [lang, selection.course_type_id, selection.language_id]);

  useEffect(() => {
    if (!user) return;
    setInfo((prev) => ({
      ...prev,
      full_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || prev.full_name,
      email: user.email || prev.email,
      phone: user.phone || prev.phone,
      country_code: user.country_code || prev.country_code,
    }));
  }, [user]);

  const totalQ = questions.length;
  const canStartTest = Boolean(selection.language_id && totalQ > 0 && !loadingQuestions);
  const progress = totalQ > 0 ? ((currentQ + 1) / totalQ) * 100 : 0;
  const answered = Object.keys(answers).length;
  const currentQuestion = questions[currentQ];
  const currentSection = resolveLocalizedValue(currentQuestion?.section, lang);
  const currentSectionTranslationKey = currentSection ? `placement.${currentSection}` : '';
  const currentSectionTranslated = currentSectionTranslationKey ? t(currentSectionTranslationKey) : '';
  const currentSectionLabel = !currentSection
    ? ''
    : currentSectionTranslated === currentSectionTranslationKey
      ? currentSection
      : currentSectionTranslated;
  const selectedLanguage = languages.find((item) => String(item.id) === String(selection.language_id));
  const placementLanguageName = getLocalizedLanguageName(selectedLanguage, lang);
  const placementTitle = placementLanguageName
    ? pickText(lang, `اختبار تحديد مستوى ${placementLanguageName}`, `${placementLanguageName} Placement Test`)
    : t('placement.title');
  const placementSubtitle = placementLanguageName
    ? pickText(lang, `اكتشف مستواك الحالي في ${placementLanguageName} من خلال اختبارنا الشامل.`, `Discover your current ${placementLanguageName} level with our comprehensive placement test.`)
    : t('placement.subtitle');
  const placementResultsSubtitle = placementLanguageName
    ? pickText(lang, `بناءً على إجاباتك، هذا مستواك المُقيَّم في ${placementLanguageName}.`, `Based on your answers, here's your assessed ${placementLanguageName} level.`)
    : t('placement.results_sub');
  const sectionLabels = [...new Set(
    questions
      .map((question) => {
        const sectionValue = resolveLocalizedValue(question?.section, lang);
        const sectionKey = sectionValue ? `placement.${sectionValue}` : '';
        const translated = sectionKey ? t(sectionKey) : '';
        return translated && translated !== sectionKey ? translated : sectionValue;
      })
      .filter(Boolean)
  )];
  const placementInfoDuration = pickText(lang, `${totalQ} سؤال، حوالي 15-20 دقيقة`, `${totalQ} questions, about 15-20 minutes`);
  const placementInfoCoverage = sectionLabels.length > 0
    ? pickText(lang, `يغطي ${sectionLabels.join('، ')}`, `Covers ${sectionLabels.join(', ')}`)
    : t('placement.info2');

  const handleAnswer = (value) => {
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: parseInt(value) }));
  };

  const goNext = () => {
    if (currentQ < totalQ - 1) {
      setCurrentQ(currentQ + 1);
      return;
    }
    const payload = resolveInfo();
    if (!hasRequiredInfo(payload)) {
      setResumeAfterInfo(false);
      setStep('info');
      return;
    }
    setStep('submitting');
    submitAnswers(payload);
  };

  const goPrev = () => {
    if (currentQ > 0) setCurrentQ(currentQ - 1);
  };

  const resolveInfo = () => {
    const nameSource = (user
      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
      : info.full_name
    ).trim();
    const parts = nameSource.split(/\s+/).filter(Boolean);
    const firstName = user?.first_name || parts[0] || '';
    const lastName = user?.last_name || (parts.length > 1 ? parts.slice(1).join(' ') : (parts[0] || ''));

    return {
      full_name: nameSource,
      first_name: firstName,
      last_name: lastName,
      email: user?.email || info.email,
      phone: user?.phone || info.phone,
      country_code: user?.country_code || info.country_code,
      notes: info.notes?.trim() || '',
    };
  };

  const hasRequiredInfo = (payload) => (
    payload.full_name && payload.email && payload.phone
  );

  const submitAnswers = async (payload) => {
    setLoading(true);
    try {
      const payloadWithSelections = { ...payload, answers };
      if (selection.language_id) payloadWithSelections.language_id = parseInt(selection.language_id, 10);
      if (selection.course_type_id) payloadWithSelections.course_type_id = parseInt(selection.course_type_id, 10);
      delete payloadWithSelections.full_name;
      const res = await api.post('/placement-test/submit', payloadWithSelections);
      setResult(res.data);
      setStep('result');
    } catch {
      toast.error(t('alerts.submit_failed'));
      setStep('info');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = resolveInfo();
    if (!hasRequiredInfo(payload)) {
      toast.error(t('alerts.required_fields'));
      return;
    }
    if (resumeAfterInfo) {
      setResumeAfterInfo(false);
      if (templateIntro && templateIntro.trim()) {
        setStep('intro');
        setShowIntro(true);
        return;
      }
      setStep('test');
      return;
    }
    setStep('submitting');
    submitAnswers(payload);
  };

  const getLevelColor = (level) => {
    const colors = {
      A1: 'bg-[hsl(var(--accent))] text-[hsl(var(--foreground))]',
      A2: 'bg-[hsl(var(--accent))] text-[hsl(var(--foreground))]',
      B1: 'bg-primary/12 text-primary',
      B2: 'bg-primary/16 text-primary',
      C1: 'bg-[hsl(var(--foreground))]/10 text-[hsl(var(--foreground))]',
      C2: 'bg-primary/20 text-primary',
    };
    return colors[level] || 'bg-muted text-muted-foreground';
  };

  const startTest = () => {
    setShowIntro(false);
    setStep('test');
  };

  useEffect(() => {
    if (!canStartTest || searchParams.get('start') !== '1' || step !== 'intro') {
      return;
    }
    if (templateIntro && templateIntro.trim()) {
      setShowIntro(true);
      return;
    }
    setStep('test');
  }, [canStartTest, searchParams, showIntro, step, templateIntro]);

  const handleStartClick = () => {
    if (!canStartTest) return;
    const payload = resolveInfo();
    if (!hasRequiredInfo(payload)) {
      setResumeAfterInfo(true);
      setStep('info');
      return;
    }
    if (templateIntro && templateIntro.trim()) {
      setShowIntro(true);
      return;
    }
    setStep('test');
  };

  return (
    <div data-testid="placement-test-page" className="bf-page-canvas bf-placement-page bg-[#f4f5f7] text-[#1c2333] dark:bg-[#1c2333] dark:text-[#f4f5f7]">
      {/* Intro */}
      {step === 'intro' && (
        <>
          <section className="bf-placement-hero relative overflow-hidden py-14 sm:py-20" style={{
            backgroundImage: 'radial-gradient(1200px circle at 15% 10%, hsl(200 85% 92% / 0.45) 0%, transparent 55%)'
          }}>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="max-w-2xl">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Badge variant="secondary" className="bf-ui-pill mb-4">{t('placement.free')}</Badge>
                  <div className="bf-placement-selector mb-6 max-w-sm">
                    <div>
                      <Label className="bf-ui-label mb-2 block text-sm font-medium">{t('pref.language')}</Label>
                      <Select
                        value={selection.language_id}
                        onValueChange={(value) => {
                          resetPlacementFlow();
                          setSelection({ language_id: value, course_type_id: '' });
                        }}
                      >
                        <SelectTrigger className="rounded-xl border border-[#d8dde6] bg-white/85 text-[#231f20] dark:border-white/12 dark:bg-[#161c27] dark:text-white">
                          <SelectValue placeholder={t('pref.select_language')} />
                        </SelectTrigger>
                        <SelectContent className="border border-[#d8dde6] bg-white text-[#231f20] dark:border-white/12 dark:bg-[#161c27] dark:text-white">
                          {languages.map((languageOption) => (
                            <SelectItem key={languageOption.id} value={String(languageOption.id)}>
                              {getLocalizedLanguageName(languageOption, lang)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <h1 className="bf-page-display max-w-3xl">
                    {placementTitle}
                  </h1>
                  <p className="bf-page-copy mt-4 max-w-xl text-[#1c2333]/72 dark:text-[#f4f5f7]/74">
                    {placementSubtitle} {totalQ} {t("placement.question").toLowerCase()}.
                  </p>
                  <div className="bf-placement-feature-list mt-8 space-y-4">
                    {[
                      { icon: Clock, text: placementInfoDuration },
                      { icon: CheckCircle2, text: placementInfoCoverage },
                      { icon: HelpCircle, text: t('placement.info3') },
                    ].map(({ icon: Icon, text }) => (
                      <div key={text} className="bf-placement-feature flex items-center gap-3 text-sm">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    size="lg"
                    className="bf-premium-cta mt-8 rounded-full bg-[#6c8bc6] px-8 text-white hover:bg-[#5876b0]"
                    onClick={handleStartClick}
                    disabled={!canStartTest}
                    data-testid="placement-test-start-button"
                  >
                    {t('placement.start')} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              </div>
            </div>
          </section>
          <Dialog open={showIntro} onOpenChange={setShowIntro}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t('placement.intro_title')}</DialogTitle>
                <DialogDescription>{t('placement.intro_sub')}</DialogDescription>
              </DialogHeader>
              <Separator />
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: templateIntro }} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowIntro(false)}>{t('common.cancel')}</Button>
                <Button onClick={startTest}>{t('placement.intro_ok')}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Test Questions */}
      {step === 'test' && currentQuestion && (
        <section className="bf-placement-workspace py-8 sm:py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">{currentSectionLabel || currentSection}</Badge>
                  <span className="text-sm text-muted-foreground">{t('placement.question')} {currentQ + 1} {t('placement.of')} {totalQ}</span>
                </div>
                <span className="text-sm font-medium">{answered} {t('placement.answered')}</span>
              </div>
              <Progress value={progress} className="h-2" data-testid="placement-test-progress" />
            </div>

            {/* Question */}
            <motion.div key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
              <Card className="bf-placement-card rounded-2xl border shadow-sm">
                <CardContent className="p-6 sm:p-8">
                  <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wide font-medium">{currentSectionLabel || currentSection}</p>
                  <h2 className="text-lg sm:text-xl font-semibold mb-6 leading-relaxed">{currentQuestion.question_text}</h2>
                  <RadioGroup
                    value={answers[currentQuestion.id]?.toString() || ''}
                    onValueChange={handleAnswer}
                    className="space-y-3"
                  >
                    {currentQuestion.options.map((opt, i) => (
                      <div key={i} className={`bf-placement-answer flex items-center space-x-3 rounded-xl border p-4 cursor-pointer transition-colors duration-150 ${
                        answers[currentQuestion.id] === i ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                      }`} data-testid="placement-test-answer-option">
                        <RadioGroupItem value={i.toString()} id={`opt-${i}`} />
                        <Label htmlFor={`opt-${i}`} className="cursor-pointer text-sm flex-1">{opt}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            </motion.div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <Button variant="ghost" className="rounded-xl gap-2" onClick={goPrev} disabled={currentQ === 0}>
                <ChevronLeft className="h-4 w-4" /> {t('placement.previous')}
              </Button>
              <div className="flex items-center gap-1.5 flex-wrap justify-center max-w-[200px] sm:max-w-xs">
                {/* Show compact page numbers instead of dots */}
                {(() => {
                  const pages = [];
                  const total = questions.length;
                  const curr = currentQ;
                  
                  // Always show first page
                  pages.push(0);
                  
                  // Show range around current
                  const start = Math.max(1, curr - 1);
                  const end = Math.min(total - 2, curr + 1);
                  
                  if (start > 1) pages.push('...');
                  for (let i = start; i <= end; i++) pages.push(i);
                  if (end < total - 2) pages.push('...');
                  
                  // Always show last page
                  if (total > 1) pages.push(total - 1);
                  
                  return pages.map((p, idx) => (
                    p === '...' ? (
                      <span key={`dots-${idx}`} className="text-xs text-muted-foreground px-0.5">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentQ(p)}
                        className={`h-7 w-7 rounded-lg text-xs font-medium transition-colors ${
                          p === curr
                            ? 'bg-primary text-primary-foreground'
                            : answers[questions[p]?.id] !== undefined
                              ? 'bg-primary/15 text-primary hover:bg-primary/25'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {p + 1}
                      </button>
                    )
                  ));
                })()}
              </div>
              <Button className="rounded-xl gap-2" onClick={goNext}>
                {currentQ === totalQ - 1 ? t('placement.finish') : t('placement.next')} <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Info Collection */}
      {step === 'info' && (
        <section className="bf-placement-workspace py-12 sm:py-16">
          <div className="mx-auto max-w-lg px-4 sm:px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-center mb-8">
                <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
                <h2 className="text-2xl font-semibold">
                  {resumeAfterInfo
                    ? pickText(lang, 'أدخل بياناتك للبدء', 'Enter your details to begin')
                    : pickText(lang, 'اكتمل الاختبار', 'Test completed')}
                </h2>
                <p className="text-muted-foreground mt-2 text-sm">
                  {resumeAfterInfo
                    ? pickText(lang, 'قبل بدء الاختبار، أدخل نفس بيانات طلب الحصة حتى نرسل لك النتيجة ونتواصل معك.', 'Before starting the test, enter your details so we can send your result and follow up with you.')
                    : pickText(lang, 'أدخل بياناتك لإرسال النتيجة إلى بريدك وإشعار الفريق مباشرة.', 'Enter your details to send the result to your email and notify the team immediately.')}
                </p>
              </div>
              <Card className="bf-placement-card rounded-2xl border shadow-sm">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                      className="rounded-lg"
                      placeholder={pickText(lang, 'الاسم الكامل', 'Full name')}
                      value={info.full_name}
                      onChange={e => setInfo(f => ({ ...f, full_name: e.target.value }))}
                    />
                    <Input
                      type="email"
                      className="rounded-lg"
                      placeholder={pickText(lang, 'البريد الإلكتروني', 'Email')}
                      value={info.email}
                      onChange={e => setInfo(f => ({ ...f, email: e.target.value }))}
                      data-testid="placement-info-email"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Select value={info.country_code} onValueChange={v => setInfo(f => ({ ...f, country_code: v }))}>
                          <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {countryCodes.map(c => <SelectItem key={c.code} value={c.code}>{c.code} {c.country}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          className="rounded-lg"
                          placeholder={pickText(lang, 'رقم الهاتف', 'Phone')}
                          value={info.phone}
                          onChange={e => setInfo(f => ({ ...f, phone: e.target.value }))}
                          data-testid="placement-info-phone"
                        />
                      </div>
                    </div>
                    <Input
                      className="rounded-lg"
                      placeholder={pickText(lang, 'ملاحظات إضافية', 'Additional notes')}
                      value={info.notes}
                      onChange={e => setInfo(f => ({ ...f, notes: e.target.value }))}
                    />
                    <Button type="submit" className="w-full rounded-xl h-11" disabled={loading} data-testid="placement-test-submit-button">
                      {loading
                        ? t('placement.analyzing')
                        : resumeAfterInfo
                          ? pickText(lang, 'ابدأ الاختبار', 'Start test')
                          : pickText(lang, 'أرسل النتيجة', 'Send results')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* Submitting */}
      {step === 'submitting' && (
        <section className="bf-placement-workspace py-12 sm:py-16">
          <div className="mx-auto max-w-lg px-4 sm:px-6">
            <Card className="bf-placement-card rounded-2xl border shadow-sm">
              <CardContent className="p-6 sm:p-8">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 rounded-full border-2 border-primary/30 border-t-primary animate-spin mb-4" />
                  <h2 className="text-2xl font-semibold">{t('placement.analyzing')}</h2>
                  <p className="text-sm text-muted-foreground mt-2">{t('placement.results_sub')}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Results */}
      {step === 'result' && result && (
        <section className="bf-placement-workspace py-12 sm:py-16">
          <div className="mx-auto max-w-2xl px-4 sm:px-6">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold mb-2">{t('placement.your_results')}</h2>
                <p className="text-muted-foreground text-sm">{placementResultsSubtitle}</p>
              </div>

              <Card className="bf-placement-card rounded-2xl border shadow-sm mb-6" data-testid="placement-test-result">
                <CardContent className="p-6 sm:p-8 text-center">
                  <div className={`inline-flex h-20 w-20 items-center justify-center rounded-2xl text-3xl font-bold mb-4 ${getLevelColor(result.level)}`}>
                    {result.level}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{t('placement.your_level')}: {result.level}</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t('placement.score').replace('{score}', result.score).replace('{total}', result.total).replace('{percentage}', result.percentage)}
                  </p>
                  <Progress value={result.percentage} className="h-3 mb-4 max-w-xs mx-auto" />
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">{result.message}</p>
                </CardContent>
              </Card>

              {result.recommended_courses?.length > 0 && (
                <Card className="rounded-2xl border shadow-sm mb-6">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">{t('placement.recommended')}</h3>
                    <div className="space-y-3">
                      {result.recommended_courses.map((course, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            </div>
                            <span className="text-sm font-medium">{course.title}</span>
                          </div>
                          <Badge variant="secondary" className="text-xs">{course.level}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="rounded-2xl bg-primary/5 border-primary/20">
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold mb-2">{t('placement.whats_next')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{t('placement.team_contact')}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to={`/preferences?test_id=${result.test_id || ''}`}>
                      <Button className="rounded-xl" data-testid="placement-preferences-button">
                        {t('pref.title')} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/courses">
                      <Button variant="outline" className="rounded-xl">{t('placement.view_courses')}</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}



